import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import PublicProfile from './PublicProfile';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
console.log('🔧 NotificationBell - API_URL:', API_URL);

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null); // For viewing profiles

  useEffect(() => {
    fetchUnreadCount();
    // Fetch unread count every 30 seconds for real-time updates
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === 'success') {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
      if (error.response?.status === 401) {
        // Token expired, user needs to log in again
        setUnreadCount(0);
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      const response = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === 'success') {
        console.log('🔔 Fetched notifications:', response.data.notifications);
        setNotifications(response.data.notifications);
        // Update unread count based on actual notifications
        const unreadNotifications = response.data.notifications.filter(n => !n.is_read);
        setUnreadCount(unreadNotifications.length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      if (error.response?.status === 401) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('❌ No auth token for marking notification as read');
        return;
      }
      
      console.log(`🔔 Marking notification ${notificationId} as read...`);
      
      const response = await axios.put(`${API_URL}/api/notifications/${notificationId}`, 
        { is_read: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log(`✅ Notification ${notificationId} marked as read:`, response.data);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Notification ID:', notificationId);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      // Try new endpoint to avoid conflicts
      const fullUrl = `${API_URL}/api/notifications/clear-all`;
      console.log('🔔 Clear all notifications - Full URL:', fullUrl);
      console.log('🔔 API_URL:', API_URL);
      
      await axios.put(fullUrl, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })));
      setUnreadCount(0);
      
      // Refresh unread count from server after a small delay to ensure DB commit is complete
      setTimeout(() => {
        fetchUnreadCount();
      }, 500);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Token present:', !!localStorage.getItem('access_token'));
    }
  };

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'network_addition':
        return '👥';
      case 'review_received':
        return '⭐';
      default:
        return '🔔';
    }
  };

  const handleNotificationClick = (notification) => {
    console.log('🔔 Notification clicked:', notification);
    console.log('🔔 Notification ID:', notification.id);
    console.log('🔔 Notification is_read:', notification.is_read);
    console.log('🔔 Notification type:', notification.type);
    console.log('🔔 From user ID:', notification.from_user_id);
    
    // Mark as read
    if (!notification.is_read) {
      console.log('🔔 Notification is unread, marking as read...');
      markAsRead(notification.id);
    } else {
      console.log('🔔 Notification is already read, skipping mark as read');
    }

    // Handle different notification types
    if (notification.type === 'network_addition' && notification.from_user_id) {
      console.log('🔔 Opening profile for user ID:', notification.from_user_id);
      // Open the profile of the person who added them
      setSelectedUserId(notification.from_user_id);
      setIsOpen(false); // Close notification dropdown
    } else if (notification.type === 'review_received') {
      console.log('🔔 Review notification clicked');
      // For reviews, could navigate to own profile or review details
      // For now, let's close the notification (can be enhanced later)
      setIsOpen(false);
    } else {
      console.log('🔔 Unhandled notification type or missing from_user_id');
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBellClick}
        className="relative p-2 hover:bg-gray-100"
      >
        <Bell className="w-5 h-5" style={{color: 'var(--brand-navy)'}} />
        {unreadCount > 0 && (
          <Badge
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs text-white border-2 border-white"
            style={{background: 'var(--brand-red)'}}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-hidden z-50">
          <Card className="shadow-xl border-2 bg-white">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg" style={{color: 'var(--brand-navy)'}}>
                  Notifications
                </CardTitle>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={markAllAsRead}
                      className="text-xs"
                      style={{color: 'var(--brand-tan)'}}
                    >
                      Mark all read
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-gray-100"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{borderColor: 'var(--brand-navy)'}}></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                        !notification.is_read ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-lg">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <div 
                                className="w-2 h-2 rounded-full ml-2 flex-shrink-0"
                                style={{background: 'var(--brand-red)'}}
                              ></div>
                            )}
                          </div>
                          {notification.message && (
                            <p className="text-xs text-gray-600 mt-1">
                              {notification.message}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Overlay to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* PublicProfile Modal for viewing user profiles from notifications */}
      {selectedUserId && (
        <PublicProfile
          userId={selectedUserId}
          isModal={true}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
};

export default NotificationBell;